import { useEffect, useRef, useState } from 'react';

import { INCORRECT_STATE_ERROR_MSG } from '@/constants/general';
import useGetValidStates from '@/hooks/useGetValidStates';

import {
  PlaceAddress,
  PlaceCoords,
  PlacePrediction,
  getPlaceAddress,
  searchPlaces,
} from '../../../utils/googlePlaces';
import {
  AddressRow,
  AutocompleteWrap,
  Backdrop,
  BrowseLink,
  CancelLink,
  CloseButton,
  ErrorLine,
  FieldLabel,
  FieldStack,
  Footer,
  ModalCard,
  ModalHeader,
  ModalInput,
  ModalSelect,
  ModalTextArea,
  ModalTitle,
  PrimaryButton,
  SuggestionMain,
  SuggestionPanel,
  SuggestionRow,
  SuggestionSecondary,
} from './styles';

export type LocationDraft = {
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  nickname: string;
  how_to_get_there: string;
  coords: PlaceCoords | null;
};

const EMPTY_LOCATION: LocationDraft = {
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  nickname: '',
  how_to_get_there: '',
  coords: null,
};

const STATE_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

type AddLocationModalProps = {
  isOpen: boolean;
  saving?: boolean;
  errorMessage?: string | null;
  initialDraft?: Partial<LocationDraft> | null;
  onCancel: () => void;
  onAdd: (location: LocationDraft) => void;
  onBrowseSaved?: () => void;
};

const AddLocationModal = ({
  isOpen,
  saving = false,
  errorMessage = null,
  initialDraft = null,
  onCancel,
  onAdd,
  onBrowseSaved,
}: AddLocationModalProps) => {
  const [draft, setDraft] = useState<LocationDraft>(EMPTY_LOCATION);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextSearchRef = useRef(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Mirrors V1's CreateAddressModal — the active-region list comes from
  // the same source-of-truth `activeRegionStatesVar` populated by Root
  // on app boot, falling back to `allowedStates` when not yet loaded.
  // Keeping the modal's check here (rather than at the JobTypeStep
  // call site) matches V1's pattern and keeps the inline error close to
  // the field it describes.
  const validStates = useGetValidStates();
  const stateUnsupported =
    draft.state.trim().length > 0 && !validStates.includes(draft.state);

  useEffect(() => {
    if (isOpen) {
      setDraft({ ...EMPTY_LOCATION, ...(initialDraft ?? {}) });
      setPredictions([]);
      setShowSuggestions(false);
      setHighlight(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!isOpen) return null;

  const ADDRESS_FIELDS: Array<keyof LocationDraft> = [
    'address_line1',
    'city',
    'state',
    'zip',
  ];

  const update = <K extends keyof LocationDraft>(
    field: K,
    value: LocationDraft[K],
  ) =>
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (ADDRESS_FIELDS.includes(field)) next.coords = null;
      return next;
    });

  const runSearch = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setPredictions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const results = await searchPlaces(query);
      setPredictions(results);
      setShowSuggestions(results.length > 0);
      setHighlight(-1);
    }, 250);
  };

  const handleAddressInput = (value: string) => {
    update('address_line1', value);
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    runSearch(value);
  };

  const applyPrediction = async (prediction: PlacePrediction) => {
    skipNextSearchRef.current = true;
    setShowSuggestions(false);
    setPredictions([]);
    try {
      const address = await getPlaceAddress(prediction.place_id);
      if (!address) return;
      setDraft((prev) => ({
        ...prev,
        address_line1: address.address_line1 || prediction.main_text,
        city: address.city,
        state: address.state,
        zip: address.zip,
        coords: address.coords,
      }));
    } catch {
      setDraft((prev) => ({
        ...prev,
        address_line1: prediction.description,
        coords: null,
      }));
    }
  };

  const handleAddressKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (!showSuggestions || predictions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => (h + 1) % predictions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => (h <= 0 ? predictions.length - 1 : h - 1));
    } else if (event.key === 'Enter' && highlight >= 0) {
      event.preventDefault();
      applyPrediction(predictions[highlight]);
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const canAdd =
    draft.address_line1.trim().length > 0 &&
    draft.city.trim().length > 0 &&
    draft.state.trim().length > 0 &&
    draft.zip.trim().length > 0 &&
    draft.nickname.trim().length > 0 &&
    !stateUnsupported;

  const handleAdd = () => {
    if (!canAdd || saving) return;
    onAdd(draft);
  };

  return (
    <Backdrop
      onMouseDown={(event) =>
        event.target === event.currentTarget && onCancel()
      }
    >
      <ModalCard role="dialog" aria-label="Add location">
        <ModalHeader>
          <ModalTitle>Add location</ModalTitle>
          <CloseButton type="button" aria-label="Close" onClick={onCancel}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 20, height: 20 }}
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </CloseButton>
        </ModalHeader>

        <FieldStack>
          <FieldLabel>
            Address line 1
            <AutocompleteWrap ref={wrapRef}>
              <ModalInput
                placeholder="Street address"
                autoComplete="off"
                value={draft.address_line1}
                onChange={(event) => handleAddressInput(event.target.value)}
                onFocus={() =>
                  predictions.length > 0 && setShowSuggestions(true)
                }
                onKeyDown={handleAddressKeyDown}
              />
              {showSuggestions && predictions.length > 0 ? (
                <SuggestionPanel>
                  {predictions.map((prediction, index) => (
                    <SuggestionRow
                      key={prediction.place_id}
                      highlighted={index === highlight}
                      onMouseEnter={() => setHighlight(index)}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        applyPrediction(prediction);
                      }}
                    >
                      <SuggestionMain>{prediction.main_text}</SuggestionMain>
                      {prediction.secondary_text ? (
                        <SuggestionSecondary>
                          {prediction.secondary_text}
                        </SuggestionSecondary>
                      ) : null}
                    </SuggestionRow>
                  ))}
                </SuggestionPanel>
              ) : null}
            </AutocompleteWrap>
          </FieldLabel>

          <FieldLabel>
            Address line 2
            <ModalInput
              placeholder="Suite, floor, unit..."
              value={draft.address_line2}
              onChange={(event) => update('address_line2', event.target.value)}
            />
          </FieldLabel>

          <AddressRow>
            <FieldLabel>
              City
              <ModalInput
                placeholder="Enter city"
                value={draft.city}
                onChange={(event) => update('city', event.target.value)}
              />
            </FieldLabel>
            <FieldLabel>
              State
              <ModalSelect
                value={draft.state}
                onChange={(event) => update('state', event.target.value)}
              >
                <option value="">State</option>
                {STATE_CODES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </ModalSelect>
            </FieldLabel>
            <FieldLabel>
              Zip
              <ModalInput
                placeholder="20024"
                value={draft.zip}
                onChange={(event) => update('zip', event.target.value)}
              />
            </FieldLabel>
          </AddressRow>

          <FieldLabel>
            Set nickname
            <ModalInput
              placeholder="e.g. Main hall"
              value={draft.nickname}
              onChange={(event) => update('nickname', event.target.value)}
            />
          </FieldLabel>

          <FieldLabel>
            How to get there
            <ModalTextArea
              placeholder="Tell your workers what to do or where to go to start their shift"
              value={draft.how_to_get_there}
              onChange={(event) =>
                update('how_to_get_there', event.target.value)
              }
            />
          </FieldLabel>

          {onBrowseSaved ? (
            <BrowseLink type="button" onClick={onBrowseSaved} disabled={saving}>
              Browse saved
            </BrowseLink>
          ) : null}

          {stateUnsupported ? (
            <ErrorLine role="alert">{INCORRECT_STATE_ERROR_MSG}</ErrorLine>
          ) : null}
          {errorMessage ? <ErrorLine>{errorMessage}</ErrorLine> : null}
        </FieldStack>

        <Footer>
          <CancelLink type="button" onClick={onCancel} disabled={saving}>
            Cancel
          </CancelLink>
          <PrimaryButton
            type="button"
            disabled={!canAdd || saving}
            onClick={handleAdd}
          >
            {saving ? 'Saving…' : 'Add'}
          </PrimaryButton>
        </Footer>
      </ModalCard>
    </Backdrop>
  );
};

export default AddLocationModal;
